import React from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import { ArrowRight, Storefront, Wrench, Heart, Airplane, Car, CheckCircle, XCircle } from "@phosphor-icons/react";

// Public-facing comparison page for the six Curb Crew card types.
// Goal: give a buyer in a hardware/retail aisle a 60-second read on which
// card matches their hustle. Keep copy concrete, no marketing fluff. Each
// card lists the features it shows on the public profile.
//
// IMPORTANT: when adding/removing features here, mirror the conditional
// renders in CardProfile.jsx / Asset.jsx / CardholderDashboard.jsx — this
// page is the source of truth shown to buyers.

export const CARD_TYPES = [
  {
    id: "curb_crew",
    icon: Storefront,
    title: "Curb Crew",
    tagline: "Tips, fast.",
    audience: "Car guards · Petrol attendants · Waiters · Vendors",
    accent: "bg-[color:var(--cc-yellow)]",
    pitch: "The tip jar in your lanyard. Quick-pay chips for R5–R40, PayShap to your bank, no card machine.",
    features: {
      "Quick-pay chips (R5–R40)": true,
      "PayShap money to my bank": true,
      "Profile photo + skills": true,
      "Lead form": true,
      "Social media links": false,
      "Document portfolio": false,
      "Asset QR stickers": false,
      "Single-purpose details": false,
    },
  },
  {
    id: "curb_pro",
    icon: Wrench,
    title: "Curb Pro",
    tagline: "Skills + payment + repeat callbacks.",
    audience: "Plumbers · Electricians · Tradies · Mobile services",
    accent: "bg-black text-white",
    pitch: "Show your skills, take payment, and slap an Asset QR sticker on every geyser you install. Eighteen months later the leak calls you, not the plumber down the road.",
    features: {
      "Quick-pay chips (R5–R40)": "optional",
      "PayShap money to my bank": true,
      "Profile photo + skills": true,
      "Lead form": true,
      "Social media links": true,
      "Document portfolio": true,
      "Asset QR stickers": true,
      "Single-purpose details": false,
    },
  },
  {
    id: "curb_assets",
    icon: Storefront,
    title: "Curb Retailer",
    tagline: "Shopfront + stock first.",
    audience: "Clothing stores · Electronics shops · Boutiques · Bookstores",
    accent: "bg-[#C2410C] text-white",
    pitch: "Retailer card with Asset stickers for every product, rack or display you sell. Shopper scans the tag, sees your catalogue, hours and 'Reserve this item' button — no tip jar in sight.",
    features: {
      "Quick-pay chips (R5–R40)": false,
      "PayShap money to my bank": "off by default",
      "Profile photo + skills": true,
      "Lead form": true,
      "Social media links": true,
      "Document portfolio": true,
      "Asset QR stickers": true,
      "Single-purpose details": false,
    },
  },
  {
    id: "curb_medic",
    icon: Heart,
    title: "Curb Medic",
    tagline: "One job, done in 2 seconds.",
    audience: "Medical alert · Allergy ID · Emergency contacts · Pet ID",
    accent: "bg-[#DC2626] text-white",
    pitch: "Single-purpose card built to be read in a crisis. Allergies, blood type, emergency contact and meds — nothing else clutters the screen.",
    features: {
      "Quick-pay chips (R5–R40)": false,
      "PayShap money to my bank": false,
      "Profile photo + skills": "type-specific",
      "Lead form": true,
      "Social media links": false,
      "Document portfolio": "medical only",
      "Asset QR stickers": false,
      "Single-purpose details": true,
    },
  },
  {
    id: "curb_traveller",
    icon: Airplane,
    title: "Curb Traveller",
    tagline: "Trip-ready in your wallet.",
    audience: "Tour guides · Drivers · Long-distance traveller backups",
    accent: "bg-[#0EA5E9] text-white",
    pitch: "Carry trip details, driver credentials and an emergency contact in a card a stranger can scan if you can't speak. Paste a Google Drive folder of your travel docs into the secure Doc Vault — visitors unlock it with a 6-digit PIN we email to YOU, never to them.",
    features: {
      "Quick-pay chips (R5–R40)": false,
      "PayShap money to my bank": "optional",
      "Profile photo + skills": true,
      "Lead form": true,
      "Social media links": true,
      "Document portfolio": "Doc Vault unlock",
      "Asset QR stickers": false,
      "Single-purpose details": "Doc Vault PIN",
    },
  },
  {
    id: "curb_auto",
    icon: Car,
    title: "Curb Auto",
    tagline: "Retention sticker for tire & battery shops.",
    audience: "Tire shops · Battery shops · Auto service centres",
    accent: "bg-[#16A34A] text-white",
    pitch: "Stick a QR on the customer's dashboard, log what you fitted, and we'll surface 'due-soon' customers + let you push specials by email — free, forever. Customer + emergency responders see insurance + tow + license expiry; your service log stays workshop-internal.",
    features: {
      "Quick-pay chips (R5–R40)": false,
      "PayShap money to my bank": false,
      "Profile photo + skills": true,
      "Lead form": true,
      "Social media links": false,
      "Document portfolio": true,
      "Asset QR stickers": true,
      "Single-purpose details": "Due-soon inbox + push",
    },
  },
];

// Compact YES/NO/string cell. Strings render as muted text (e.g. "optional").
const Cell = ({ v }) => {
  if (v === true) return <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle weight="fill" size={14} /> Yes</span>;
  if (v === false) return <span className="inline-flex items-center gap-1 text-neutral-400"><XCircle weight="fill" size={14} /> Hidden</span>;
  return <span className="text-neutral-700 text-xs">{v}</span>;
};

export default function CardCompare() {
  // Pull a stable union of feature keys from Curb Pro (the richest type) so
  // the comparison table rows stay in a sensible order regardless of dict
  // insertion order on any individual card.
  const featureKeys = Object.keys(CARD_TYPES.find((t) => t.id === "curb_pro").features);

  return (
    <div>
      <Nav />

      {/* Hero */}
      <section className="border-b border-black bg-[color:var(--cc-bg-2)]" data-testid="compare-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="label-tag mb-4 inline-block bg-[color:var(--cc-yellow)] px-2 py-1">Curb Crew · Card Types</div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            One card style for every kind of <span className="bg-[color:var(--cc-yellow)] px-2">hustle</span>.
          </h1>
          <p className="mt-6 text-lg text-neutral-700 max-w-2xl">
            Same physical sticker. Six different public profiles. Pick one when you activate,
            switch any time from your dashboard.
          </p>
        </div>
      </section>

      {/* Card grid */}
      <section className="border-b border-black bg-white" data-testid="compare-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CARD_TYPES.map((t) => {
              const Icon = t.icon;
              return (
                <article
                  key={t.id}
                  className="border-2 border-black bg-white brutal-shadow flex flex-col"
                  data-testid={`compare-card-${t.id}`}
                >
                  <header className={`${t.accent} border-b-2 border-black p-4 flex items-start gap-3`}>
                    <Icon size={28} weight="bold" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-display font-black text-xl tracking-tight">{t.title}</h2>
                        {t.badge && (
                          <span className="label-tag bg-white text-black border border-black px-1.5 py-0.5 text-[10px]">{t.badge}</span>
                        )}
                      </div>
                      <div className="text-sm mt-1 opacity-90">{t.tagline}</div>
                    </div>
                  </header>
                  <div className="p-4 flex flex-col flex-1">
                    <div className="label-tag text-neutral-500">Who it&apos;s for</div>
                    <div className="text-sm text-neutral-800 mt-1">{t.audience}</div>
                    <p className="text-sm text-neutral-700 leading-relaxed mt-3">{t.pitch}</p>
                    <ul className="mt-4 space-y-1 text-xs">
                      {featureKeys.map((k) => (
                        <li key={k} className="flex items-center justify-between gap-3 border-b border-dashed border-neutral-200 py-1">
                          <span className="text-neutral-600">{k}</span>
                          <Cell v={t.features[k]} />
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Side-by-side table for desktop nerds who want the matrix view */}
      <section className="border-b border-black bg-[color:var(--cc-bg-2)]" data-testid="compare-table-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="label-tag">Feature matrix</div>
              <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-1">All six, side by side.</h3>
            </div>
            <div className="font-mono-cc text-xs text-neutral-500">CURB-CARD / COMPARE_V1</div>
          </div>
          <div className="overflow-x-auto border-2 border-black bg-white">
            <table className="w-full text-xs" data-testid="compare-table">
              <thead className="bg-black text-white">
                <tr>
                  <th className="text-left label-tag p-3 whitespace-nowrap">Feature</th>
                  {CARD_TYPES.map((t) => (
                    <th key={t.id} className="text-left label-tag p-3 whitespace-nowrap">{t.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {featureKeys.map((k) => (
                  <tr key={k} className="border-t border-neutral-300">
                    <td className="p-3 align-top text-neutral-700 whitespace-nowrap">{k}</td>
                    {CARD_TYPES.map((t) => (
                      <td key={t.id} className="p-3 align-top whitespace-nowrap" data-testid={`compare-cell-${t.id}-${k.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                        <Cell v={t.features[k]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-black bg-white" data-testid="compare-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Pick one when you activate.</h3>
          <p className="mt-3 text-neutral-700 max-w-xl mx-auto">
            You can switch card type from your dashboard any time — your card code, scans, and leads
            stay with you.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/lookup" className="label-tag px-5 py-3 border border-black brutal-btn" data-testid="compare-find-btn">
              Find a Curb Crew <ArrowRight size={14} weight="bold" className="ml-2 inline" />
            </Link>
            <Link to="/cardholder/login" className="label-tag px-5 py-3 bg-black text-white brutal-btn" data-testid="compare-mycard-btn">
              Manage my card <ArrowRight size={14} weight="bold" className="ml-2 inline" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
